import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Search } from 'lucide-react';
import { AnimatedSection } from '@/components/AnimatedComponents';
import { BlogCard } from '@/components/blog/BlogCard';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function Blog() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  const fetchPosts = useCallback(async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/blog`);
      setPosts(response.data);
    } catch (error) {
      console.error('Failed to fetch blog posts:', error);
      /* placeholder posts are shown when fetch fails */
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPosts();
  }, [fetchPosts]);

  const filteredPosts = posts.filter(post =>
    post.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    post.excerpt.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Placeholder posts for when there's no content yet
  const placeholderPosts = [
    {
      id: 'placeholder-1',
      title: 'Fightback Farmers: Feeding Australia Together',
      slug: 'fightback-farmers',
      excerpt: 'Working with Tara from GoCollaborate and Steve Bibb from Barking Mad Productions on this ABC documentary...',
      featured_image: 'https://customer-assets.emergentagent.com/job_site-clone-platform/artifacts/08gi0cgw_Terry_commercial_FBF_02.jpg',
      tags: ['Documentary', 'ABC', 'Farming'],
      created_at: '2025-03-30',
    },
    {
      id: 'placeholder-2',
      title: 'Washed Away',
      slug: 'washed-away-documentary',
      excerpt: 'A documentary following communities in the Northern Rivers rebuilding after the 2022 floods.',
      featured_image: 'https://customer-assets.emergentagent.com/job_site-clone-platform/artifacts/tetfj4m6_Terry_FFH_photo_01.jpg',
      tags: ['Documentary', 'Washed Away'],
      created_at: '2022-07-04',
    },
    {
      id: 'placeholder-3',
      title: 'Welcome to Terry Webster',
      slug: 'welcome',
      excerpt: 'A brief introduction to my work as a filmmaker, cinematographer and producer.',
      featured_image: 'https://customer-assets.emergentagent.com/job_site-clone-platform/artifacts/eyoeieqm_Terry_aboutme_photo_02.jpg',
      tags: ['Introduction', 'About'],
      created_at: '2025-03-29',
    },
  ];

  const displayPosts = posts.length > 0 ? filteredPosts : placeholderPosts;

  // JSON-LD structured data for SEO
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Blog",
    "name": "Terry Webster Blog",
    "description": "Insights on filmmaking, cinematography, and documentary production",
    "url": "https://terrywebster.com.au/blog",
    "author": {
      "@type": "Person",
      "name": "Terry Webster",
      "jobTitle": "Filmmaker & Cinematographer"
    },
    "blogPost": displayPosts.map(post => ({
      "@type": "BlogPosting",
      "headline": post.title,
      "description": post.excerpt,
      "datePublished": post.created_at,
      "image": post.featured_image,
      "author": {
        "@type": "Person",
        "name": "Terry Webster"
      }
    }))
  };

  return (
    <main data-testid="blog-page">
      {/* SEO Structured Data (escaped to prevent script-tag breakout) */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(structuredData).replace(/</g, '\\u003c').replace(/&/g, '\\u0026'),
        }}
      />

      {/* Hero */}
      <section className="pt-32 pb-16 md:pt-40 md:pb-20 bg-[#050505]">
        <div className="container-wide">
          <div className="max-w-3xl">
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-[#007AFF] text-sm font-bold uppercase tracking-widest mb-4"
            >
              Journal
            </motion.p>
            <motion.h1
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tighter mb-6"
            >
              Insights & Stories
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="text-[#A1A1AA] text-lg md:text-xl leading-relaxed mb-8"
            >
              Behind-the-scenes stories, filmmaking insights, and thoughts on the craft of visual storytelling.
            </motion.p>

            {/* Search */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="relative max-w-md"
            >
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#52525B]" />
              <input
                type="text"
                placeholder="Search articles..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-12 pr-4 py-3 bg-[#0C0C0C] border border-[#27272A] text-white placeholder-[#52525B] focus:border-[#007AFF] focus:outline-none transition-colors"
                data-testid="blog-search-input"
                aria-label="Search blog articles"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Blog Posts */}
      <section className="py-16 md:py-24 bg-[#050505]" data-testid="blog-posts-section">
        <div className="container-wide">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <div className="spinner" />
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {displayPosts.map((post, index) => (
                <BlogCard key={post.id} post={post} index={index} />
              ))}
            </div>
          )}

          {!loading && displayPosts.length === 0 && (
            <div className="text-center py-20">
              <p className="text-[#A1A1AA] text-lg">No posts found matching your search.</p>
            </div>
          )}

          {posts.length === 0 && !loading && (
            <div className="text-center mt-12 p-8 border border-[#27272A] bg-[#0C0C0C]">
              <p className="text-[#A1A1AA] mb-2">
                These are placeholder posts. Login to the admin panel to create real content.
              </p>
              <Link to="/admin/login" className="text-[#007AFF] hover:underline">
                Go to Admin →
              </Link>
            </div>
          )}
        </div>
      </section>

      {/* Newsletter CTA */}
      <section className="py-20 bg-[#0C0C0C] border-t border-[#27272A]">
        <div className="container-wide">
          <div className="max-w-2xl mx-auto text-center">
            <AnimatedSection>
              <p className="text-[#007AFF] text-sm font-bold uppercase tracking-widest mb-4">Newsletter</p>
              <h2 className="text-3xl md:text-4xl font-bold tracking-tighter mb-4">
                Stay in the Loop
              </h2>
              <p className="text-[#A1A1AA] mb-8">
                Get filmmaking insights, behind-the-scenes content, and project updates delivered to your inbox.
              </p>
              <form className="flex flex-col sm:flex-row gap-4 max-w-md mx-auto">
                <input
                  type="email"
                  placeholder="your@email.com"
                  className="flex-1 px-4 py-3 bg-[#050505] border border-[#27272A] text-white placeholder-[#52525B] focus:border-[#007AFF] focus:outline-none transition-colors"
                  aria-label="Email address for newsletter"
                />
                <button type="submit" className="btn-primary whitespace-nowrap">
                  Subscribe
                </button>
              </form>
            </AnimatedSection>
          </div>
        </div>
      </section>
    </main>
  );
}
